import { branchNameMap } from "../../../../core/schema/Branch.js";
import { CustomerType } from "../../../../core/schema/CustomerType.js";
import { DeliveryType } from "../../../../core/schema/DeliveryType.js";
import type { Locale } from "../../../../core/schema/Locale.js";
import type { OrderStop } from "../../../../core/schema/OrderStop.js";
import type { ProductForCustomer } from "../../../../core/schema/Product.js";
import type { ProductAmountPerCategory } from "../../../../core/schema/ProductAmountPerCategory.js";
import type { ScheduledOrderStopsSettingItem } from "../../../../core/schema/ScheduledOrderStopsSetting.js";
import { deliveryTypeToBranchMap } from "../../../../core/schema/deliveryTypeToBranchMap.js";
import { areProductQuantitiesOverMax } from "../../../../core/schema/utils/areProductQuantitiesOverMax.js";
import { getNearestDeliveryDate } from "../../../../core/schema/utils/getNearestDeliveryDate.js";
import { getProductsWithNotPermittedPickUpDateAtDate } from "../../../../core/schema/utils/getProductsWithNotPermittedPickUpDateAtDate.js";
import { isDateDuringScheduledOrderStop } from "../../../../core/schema/utils/isDateDuringScheduledOrderStop.js";
import { isDeliveryTypeInStop } from "../../../../core/schema/utils/isDeliveryTypeInStop.js";
import { isOnlineOrder } from "../../../../core/schema/utils/isOnlineOrder.js";
import { toDateTimeMonthYearFormat } from "../../../../core/utils/toDateTimeMonthYearFormat.js";
import { upperCaseFirstLetter } from "../../../../core/utils/upperCaseFirstLetter.js";
import type { Cart, CartInStep } from "../../stores/cart/Cart.js";
import { CartStep, cartStepOrderMap, cartStepPathMap } from "../../stores/cart/CartStep.js";
import type { CartStore } from "../../stores/cart/CartStore.js";
import { inflectProduct } from "./inflectProduct.js";

export function getCorrectCartInStepOrRedirect<Step extends CartStep>(
	cartStore: CartStore,
	locale: Locale,
	inStep: Step,
	cart: Cart,
	orderStops: OrderStop[],
	scheduledOrderStops: ScheduledOrderStopsSettingItem[],
	maxProductsPerCategory: ProductAmountPerCategory,
): { cartInStep: CartInStep<Step>; redirected: boolean } {
	let redirected = false;
	if (typeof window === "undefined") {
		return {
			cartInStep: cart as CartInStep<Step>,
			redirected,
		};
	}

	const cartStepIndex = cartStepOrderMap[cart.step];
	const stepIndex = cartStepOrderMap[inStep];
	const isOnline = isOnlineOrder(cart.order);
	const { deliveryDate } = cart.order;

	// Clear outdated delivery date and force user to select a new one
	const paymentDeliveryIndex = cartStepOrderMap[CartStep.PaymentDelivery];
	if (cartStepIndex >= paymentDeliveryIndex && deliveryDate && !isOnline) {
		const date = new Date(deliveryDate);
		const nearestDeliveryDate = getNearestDeliveryDate(cart.productsInOrder, CustomerType.B2c, cart.order.isExpress);
		if (nearestDeliveryDate && date < nearestDeliveryDate) {
			// eslint-disable-next-line no-alert
			alert(
				`Nejbližší možné datum vyzvednutí je ${toDateTimeMonthYearFormat(nearestDeliveryDate, locale)}, prosím zvolte jiný termín.`,
			);
			void cartStore.clearDeliveryDate();
		}
	}

	if (cartStepIndex > paymentDeliveryIndex && deliveryDate && cart.order.deliveryType !== undefined) {
		if (isDeliveryTypeInStop(cart.order.deliveryType, CustomerType.B2c, orderStops)) {
			if (cart.order.deliveryType === DeliveryType.Online) {
				// eslint-disable-next-line no-alert
				alert(`Z důvodu odstávky není možné objednat.`);
			} else {
				// eslint-disable-next-line no-alert
				alert(
					`Z důvodu odstávky není možné objednat na pobočce ${branchNameMap[deliveryTypeToBranchMap[cart.order.deliveryType]]}.`,
				);
			}
			void cartStore.clearDeliveryType();
			window.location.replace(`/${cartStepPathMap[CartStep.PaymentDelivery][locale]}`);
			redirected = true;
		}

		const selectedDateIsDuringStop = isDateDuringScheduledOrderStop(
			deliveryDate,
			cart.order.deliveryType,
			CustomerType.B2c,
			scheduledOrderStops,
		);

		if (selectedDateIsDuringStop) {
			// eslint-disable-next-line no-alert
			alert(
				"Z důvodu odstávky není možné ve zvolený den na zvoleném místě vyzvednout objednávku, prosím zvolte jiné místo či termín.",
			);
			void cartStore.clearDeliveryDate();
			void cartStore.clearDeliveryType();
			window.location.replace(`/${cartStepPathMap[CartStep.PaymentDelivery][locale]}`);
			redirected = true;
		}

		const productsWithNotPermittedPickUpDateAtDate = getProductsWithNotPermittedPickUpDateAtDate(
			deliveryDate,
			cart.productsInOrder,
		);

		if (productsWithNotPermittedPickUpDateAtDate.length > 0) {
			const formatter = new Intl.ListFormat("cs", { style: "long", type: "conjunction" });
			const productNames = productsWithNotPermittedPickUpDateAtDate.map(({ name }: ProductForCustomer) => name[locale]);
			const formattedProductNames = formatter.format(productNames);
			// eslint-disable-next-line no-alert
			alert(
				`${upperCaseFirstLetter(inflectProduct(productsWithNotPermittedPickUpDateAtDate.length))} ${formattedProductNames} nemají povolené vyzvednutí ve zvolený den, prosím zvolte jiné místo či termín.`,
			);
			void cartStore.clearDeliveryDate();
			window.location.replace(`/${cartStepPathMap[CartStep.PaymentDelivery][locale]}`);
			redirected = true;
		}
	}

	const overviewIndex = cartStepOrderMap[CartStep.Overview];
	if (stepIndex > overviewIndex) {
		for (const product of cart.order.products) {
			const productDetail = cart.productsInOrder.find(({ id }) => id === product.product.id);
			if (!productDetail) {
				throw new Error("Product not found in productsInOrder");
			}

			if (product.quantity < productDetail.minimumOrderQuantity) {
				window.location.replace(`/${cartStepPathMap[CartStep.Overview][locale]}`);
				redirected = true;
				break;
			}
		}

		if (areProductQuantitiesOverMax(cart.order.products, maxProductsPerCategory)) {
			window.location.replace(`/${cartStepPathMap[CartStep.Overview][locale]}`);
			redirected = true;
		}
	}

	if (stepIndex > paymentDeliveryIndex && !deliveryDate) {
		window.location.replace(`/${cartStepPathMap[CartStep.PaymentDelivery][locale]}`);
		redirected = true;
	}

	if (cartStepIndex < stepIndex) {
		window.location.replace(`/${cartStepPathMap[cart.step][locale]}`);
		redirected = true;
	}

	return {
		cartInStep: cart as CartInStep<Step>,
		redirected,
	};
}
